/**
 * R0.9 — Karte „Wer fragt hier an?" im Anfragen-Drawer (Konzept RECHERCHE-SICHTBARKEIT § 2.3).
 *
 * Sie steht direkt unter der Kontaktkarte und beantwortet die Leitfrage des Konzepts (§ 2.1) an
 * dem Ort, an dem ueber ein Angebot entschieden wird — statt sie im Kunden-Profil zu verstecken
 * (Befund B1). Aufklappbar und **standardmaessig zu** (Entscheidung P-1: der Drawer bleibt und
 * wird erweitert, muss also lesbar bleiben); das Klappmuster ist woertlich das aus
 * docs/LOOP-ANFRAGE-ZU-ANGEBOT.md § 5.10/§ 5.11 uebernommen — `useState(false)`, Kopf als
 * `role="button" tabIndex={0}` mit `onClick` + `onKeyDown` (Enter/Space), Chevron
 * `expand_more`/`expand_less`, Inhalt ueber `hidden`. Im Kopf steht eine Kurzinfo, damit
 * EINGEKLAPPT sichtbar bleibt, ob es ueberhaupt etwas gibt.
 *
 * ── Diese Datei RENDERT nur ─────────────────────────────────────────────────────────────────
 * Welche Angabe erscheint, woher sie stammt, von wann sie ist und wann die Karte ganz entfaellt,
 * entscheidet `firmenprofilKarte()` aus `@maestro/firmenprofil` — eine reine Funktion, getestet
 * in `apps/api/test/firmenprofil-karte.test.ts`. Den Zustandssatz baut sie ueber
 * `@maestro/anreicherung` (R0.7), damit hier keine zweite Formulierung entsteht. Eine Ratsche im
 * selben Test haelt fest, dass diese Datei die Regel aufruft statt sie nachzubauen.
 *
 * Seit 06.08.2026 hat `apps/web` einen eigenen Test-Runner: `apps/web/test/firmenprofil-karte.test.tsx`
 * prueft, was der Regeltest nicht sehen kann — dass die Herkunftszeile hier auch AUSGEGEBEN wird.
 * Ein geloeschtes `<dd>` genuegte sonst, und keine Angabe truege mehr ihre Herkunft.
 *
 * Der Ersatztext fuer eine Quelle ohne Zeitstempel steht bewusst NICHT in dieser Datei: ihn baut
 * `standZeile()` aus `@maestro/recherche-stand` (R1.6). Eine Ratsche in
 * `apps/api/test/firmenprofil-karte.test.ts` haelt fest, dass er hier nicht ein zweites Mal
 * entsteht — auch nicht als Literal in einem Kommentar.
 *
 * ── Die Karte ruft NICHTS extern ab ─────────────────────────────────────────────────────────
 * Sie liest `GET /api/inquiries/:id/firmenprofil` (R0.8) — eine reine Leseroute, die weder einen
 * externen Abruf noch einen Claim ausloest (mit Fetch-Rekorder gemessen). Das Ansehen einer
 * Anfrage darf keine Fremdlast gegen das geteilte 50/h-Budget erzeugen; der Ausloeser fuer einen
 * echten Abruf ist R2.1.
 *
 * ── Kein Knopf ohne Wirkung — und keiner zu wenig ───────────────────────────────────────────
 * Angeboten wird genau die eine Handlung, die `@maestro/anreicherung` fuer den Zustand nennt:
 * Sprung in die Modul-Kachel bei ausgeschaltetem Modul, sonst — seit R0.13 — der manuelle
 * Neuabruf („Jetzt abrufen" / „Erneut versuchen" / „Neu abrufen"). Bis R0.13 gab es dafuer keinen
 * Endpunkt, und ein Knopf ohne Wirkung waere derselbe Fehler wie ein Einheitssatz gewesen. Jetzt
 * gilt die Regel andersherum: wo ein Weg existiert, gehoert er angeboten.
 *
 * Der Neuabruf ist der EINZIGE Punkt, an dem diese Karte etwas ausloest. Er laeuft ueber
 * `POST /api/anreicherung/handelsregister` durch denselben Stundendeckel und dieselbe
 * Rollenpruefung wie der automatische Weg — das Ansehen der Anfrage loest weiterhin nichts aus.
 *
 * ── Stufe 1 (07.08.2026) ────────────────────────────────────────────────────────────────────
 * Zwei Ergaenzungen, beide ohne neue Datenquelle und ohne einen einzigen zusaetzlichen Abruf:
 * die **Google-Bewertung** erscheint als Angabe (mit Quelle, Stand und der nicht abschaltbaren
 * Attribution aus R4.5), und **je Quelle** ist ablesbar, ob ueberhaupt nachgesehen wurde —
 * Handelsregister, Google Places, Impressum, USt-IdNr.-Bestaetigung. Beide Entscheidungen liegen
 * in `@maestro/firmenprofil`; hier steht `.map()`.
 *
 * Die Quellenzeilen tragen ABSICHTLICH keine Schaltflaeche: `neu_abrufen` fuehrt ueber
 * `POST /api/anreicherung/handelsregister` und stoesst genau eine Quelle an. Ein gleich
 * aussehender Knopf an der Places- oder Impressum-Zeile taete nichts oder etwas anderes, als er
 * verspricht — dieselbe Regel, die R0.7 befolgt und R0.13 erst dann gelockert hat, als es einen
 * echten Weg gab.
 *
 * ── Bewusst NICHT enthalten ─────────────────────────────────────────────────────────────────
 * Eine KI-Kurzzusammenfassung. Sie stand in der Soll-Tabelle § 2.3, wurde hier nie gerendert und
 * ist seit R1.7 ersatzlos gestrichen (§ 6.1: § 31 BDSG / EuGH C-634/21, sql/243) — MAESTRO
 * erzeugt keine Einschaetzung. Ebenso wenig hier: Beziehungsstand (P1), Wetter (R0.11),
 * Places (R2.4).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { firmenprofilKarte, type FirmenprofilAntwort } from "@maestro/firmenprofil";
import { useApi } from "../hooks/useApi";
import { neuabrufAnstossen } from "../lib/neuabruf";
import { Icon, LoadingRows } from "./components";
import { GoogleAttribution } from "./GoogleAttribution";

/** Ziel des „Sprung in die Kachel" aus Konzept § 4.2 (settings.tsx liest ?tab= aus der URL). */
const MODUL_KACHEL_URL = "/einstellungen?tab=module";

export function FirmenprofilKarte({
  inquiryId,
  firma,
  anfrageEingang,
}: {
  inquiryId: string;
  /** Firmenname aus der Anfrage — ohne ihn wird gar nicht erst geladen. */
  firma: string | null;
  /** Eingang der Anfrage: der Stand der Angabe „Firma". */
  anfrageEingang: string;
}) {
  const [offen, setOffen] = useState(false);
  // R0.13: Zustand des Knopfes. Was danach zu sehen ist, liest die Karte aus der Route neu.
  const [laeuft, setLaeuft] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const q = useApi<FirmenprofilAntwort | null>(firma ? `/api/inquiries/${inquiryId}/firmenprofil` : null);

  // Der Hook behaelt beim Wechsel der Anfrage kurz die vorherige Antwort (bewusst, gegen Blinken —
  // hooks/ladezustand.ts). Fuer diese Karte waere das die Firma der ZULETZT geoeffneten Anfrage:
  // deshalb zaehlt nur eine Antwort, die zu genau dieser Anfrage gehoert.
  const profil = q.data && q.data.inquiryId === inquiryId ? q.data : null;
  const karte = firmenprofilKarte({ profil, anfrageEingang, jetzt: new Date() });

  if (!firma) return null;
  if (!karte.sichtbar) {
    if (q.loading) {
      return (
        <div className="rounded-xl border border-surface-variant bg-surface-container-lowest p-3.5 dark:border-white/10 dark:bg-night-card">
          <LoadingRows rows={1} />
        </div>
      );
    }
    // Scheitert der Abruf der Route, verschwindet die Karte NICHT stillschweigend — eine
    // verschwundene Karte erklaert nichts (dieselbe Lehre wie „Modul aus" in R0.7). Der Satz steht
    // bewusst hier und nicht in der Regel: er sagt etwas ueber DIESEN Abruf der eigenen API, nicht
    // ueber die Firma — er ist keine Recherche-Angabe und traegt darum auch keine Quelle/keinen Stand.
    if (q.error) {
      return (
        <div className="rounded-xl border border-surface-variant bg-surface-container-lowest px-3.5 py-2.5 dark:border-white/10 dark:bg-night-card">
          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">Wer fragt hier an?</p>
          <p className="mt-0.5 font-body-sm text-[12px] text-error">Firmenprofil konnte nicht geladen werden: {q.error}</p>
        </div>
      );
    }
    return null;
  }

  const umschalten = () => setOffen((v) => !v);

  return (
    <div className="overflow-hidden rounded-xl border border-surface-variant bg-surface-container-lowest dark:border-white/10 dark:bg-night-card">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={offen}
        onClick={umschalten}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            umschalten();
          }
        }}
        className="flex w-full cursor-pointer items-center justify-between gap-2 px-3.5 py-2.5 text-left"
      >
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">
            {karte.titel}
          </p>
          <p className="mt-0.5 truncate font-body-sm text-[12.5px] font-medium text-on-surface dark:text-inverse-on-surface">
            {karte.firma}
          </p>
          {/* Eingeklappt die einzige Aussage: gibt es etwas — und wie alt ist es? */}
          <p className="mt-0.5 truncate font-body-sm text-[11px] text-on-surface-variant/70" title={karte.kurzinfo}>
            {karte.kurzinfo}
          </p>
        </div>
        <Icon name={offen ? "expand_less" : "expand_more"} className="shrink-0 text-[18px] text-on-surface-variant/60" />
      </div>

      <div hidden={!offen} className="border-t border-surface-variant px-3.5 py-3 dark:border-white/10">
        <div className="space-y-3">
          {/* § 4.2: EIN Satz je Zustand, daneben hoechstens EINE Handlung. */}
          {karte.satz && (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <p
                className={
                  karte.zustand === "fehler"
                    ? "font-body-sm text-[12px] text-error"
                    : "font-body-sm text-[12px] text-on-surface-variant/70"
                }
              >
                {karte.satz}
              </p>
              {karte.handlung?.art === "modul_kachel" && (
                <Link to={MODUL_KACHEL_URL} className="font-label-md text-[12px] text-primary hover:underline dark:text-primary-fixed-dim">
                  {karte.handlung.beschriftung}
                </Link>
              )}
              {karte.handlung?.art === "neu_abrufen" && (
                <button
                  type="button"
                  disabled={laeuft}
                  onClick={async () => {
                    setLaeuft(true);
                    setHinweis(null);
                    const { hinweis: h } = await neuabrufAnstossen({ anfrageId: inquiryId });
                    setHinweis(h);
                    setLaeuft(false);
                    q.reload();
                  }}
                  className="font-label-md text-[12px] text-primary hover:underline disabled:opacity-50 dark:text-primary-fixed-dim"
                >
                  {laeuft ? "Wird angestoßen …" : karte.handlung.beschriftung}
                </button>
              )}
            </div>
          )}

          {/* Eine Ablehnung ist hier keine Fehlermeldung: „Stundenlimit erreicht, vorgemerkt"
              heisst, dass alles richtig lief — nur eben nicht jetzt. */}
          {hinweis && <p className="font-body-sm text-[12px] text-on-surface-variant/70">{hinweis}</p>}

          {/* Jede Angabe mit ihrer Herkunft darunter — die harte Regel dieses Loops, hier sichtbar. */}
          <dl className="space-y-2.5">
            {karte.angaben.map((a) => (
              <div key={a.label}>
                <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">{a.label}</dt>
                <dd className="font-body-sm text-[12.5px] text-on-surface dark:text-inverse-on-surface">{a.wert}</dd>
                <dd className="font-body-sm text-[11px] text-on-surface-variant/60">{a.herkunft}</dd>
              </div>
            ))}
          </dl>

          {/* Stufe 1 / R4.5 (§ 6.2): Sobald eine Google-Angabe auf dem Schirm steht — hier die
              Bewertung —, steht Google dran. Es gibt keinen Schalter dafuer: `googleWerte` sind die
              Werte selbst, und die Komponente entscheidet allein an ihnen. Steht keine Google-Angabe
              in der Karte, ist die Liste leer und die Zeile erscheint gar nicht erst. */}
          <GoogleAttribution werte={karte.googleWerte} />

          {/* ── Stufe 1: „gesucht und nichts gefunden" ≠ „nicht nachgesehen" ──────────────────
              Je Quelle eine Zeile. Bis hierher trug die Karte diese Unterscheidung nur fuers
              Handelsregister; bei den uebrigen Quellen stand bei fehlender Angabe schlicht nichts,
              und beide Faelle sahen gleich aus. Welche Zeile welchen Text bekommt, entscheidet
              ausschliesslich die geprueste Regel — hier wird gerendert, nicht formuliert. */}
          {karte.quellen.length > 0 && (
            <div className="border-t border-surface-variant pt-2.5 dark:border-white/10">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">
                Nachgeschlagen
              </p>
              <ul className="mt-1 space-y-1.5">
                {karte.quellen.map((q) => (
                  <li key={q.quelle}>
                    <p
                      className={
                        q.nachgesehen
                          ? "font-body-sm text-[11px] text-on-surface-variant/70"
                          : "font-body-sm text-[11px] text-on-surface-variant/50"
                      }
                    >
                      {q.kopf}
                    </p>
                    {q.satz && (
                      <p
                        className={
                          q.zustand === "fehler"
                            ? "font-body-sm text-[12px] text-error"
                            : "font-body-sm text-[12px] text-on-surface dark:text-inverse-on-surface"
                        }
                      >
                        {q.satz}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {karte.bundesanzeigerSuchUrl && (
            <a
              href={karte.bundesanzeigerSuchUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-label-md text-[12px] text-primary hover:underline dark:text-primary-fixed-dim"
            >
              Auf Bundesanzeiger ansehen <Icon name="open_in_new" className="text-[15px]" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
